import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  requestCompanyOwnerPasswordReset,
  resetCompanyOwnerPassword,
} from '../services/api/companyOwnerAuth';
import {
  requestCustomerPasswordReset,
  resetCustomerPassword,
} from '../services/api/customerAuth';
import {
  requestDriverPasswordReset,
  resetDriverPassword,
} from '../services/api/driverAuth';
import '../styles/register.css';

type ResetRole = 'customer' | 'driver' | 'company';
type ResetStep = 'email' | 'reset';

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function resolveRole(value: string | null): ResetRole {
  return value === 'driver' || value === 'company' ? value : 'customer';
}

function validatePassword(password: string): string {
  if (!password) return 'Password is required.';
  if (password.length < 8) return 'Password must be at least 8 characters.';
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    return 'Password must contain uppercase, lowercase, and a number.';
  }
  return '';
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<ResetRole>(resolveRole(searchParams.get('role')));
  const [step, setStep] = useState<ResetStep>('email');
  const [email, setEmail] = useState(normalizeEmail(searchParams.get('email') ?? ''));
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const roleMeta = useMemo(
    () => ({
      customer: {
        badge: 'Customer Reset',
        title: 'Reset your customer password',
        copy: 'Enter your account email and we will send a 6-digit reset code.',
        loginPath: '/login?role=customer',
      },
      driver: {
        badge: 'Driver Reset',
        title: 'Reset your driver password',
        copy: 'Use the email linked to your driver account to receive a reset code.',
        loginPath: '/login?role=driver',
      },
      company: {
        badge: 'Company Owner Reset',
        title: 'Reset your company owner password',
        copy: 'Use the email address linked to your company owner account.',
        loginPath: '/login?role=company',
      },
    }),
    [],
  );

  const meta = roleMeta[selectedRole];

  const validateEmail = () => {
    const normalized = normalizeEmail(email);
    const nextErrors: Record<string, string> = {};

    if (!normalized) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateReset = () => {
    const nextErrors: Record<string, string> = {};
    const passwordError = validatePassword(newPassword);

    if (code.trim().length !== 6) {
      nextErrors.code = 'Enter the 6-digit code sent to your email.';
    }
    if (passwordError) {
      nextErrors.newPassword = passwordError;
    }
    if (confirmPassword !== newPassword) {
      nextErrors.confirmPassword = 'Passwords do not match.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const requestResetCode = async (isResend = false) => {
    if (!validateEmail()) return;

    const normalized = normalizeEmail(email);
    setError('');
    setSuccess('');
    isResend ? setResending(true) : setLoading(true);

    try {
      const response =
        selectedRole === 'driver'
          ? await requestDriverPasswordReset(normalized)
          : selectedRole === 'company'
            ? await requestCompanyOwnerPasswordReset(normalized)
            : await requestCustomerPasswordReset(normalized);

      setEmail(normalized);
      setChallengeId(response.challengeId);
      setStep('reset');
      setSuccess(response.message || 'If the account exists, a password reset code has been sent.');
    } catch (requestError: any) {
      setError(requestError.message || 'Unable to send password reset code. Please try again.');
    } finally {
      isResend ? setResending(false) : setLoading(false);
    }
  };

  const submitNewPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challengeId) {
      setError('Please request a new reset code.');
      setStep('email');
      return;
    }
    if (!validateReset()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (selectedRole === 'driver') {
        await resetDriverPassword({
          challengeId,
          email,
          code: code.trim(),
          newPassword,
        });
      } else if (selectedRole === 'company') {
        await resetCompanyOwnerPassword({
          challengeId,
          code: code.trim(),
          newPassword,
        });
      } else {
        await resetCustomerPassword({
          challengeId,
          code: code.trim(),
          newPassword,
        });
      }

      navigate(`${meta.loginPath}&email=${encodeURIComponent(email)}&reset=success`, { replace: true });
    } catch (resetError: any) {
      setError(resetError.message || 'Unable to reset password. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchRole = (role: ResetRole) => {
    setSelectedRole(role);
    setStep('email');
    setChallengeId('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setFieldErrors({});
  };

  return (
    <main className="register-page">
      <div className="register-shell register-shell-single">
        <div className="register-panel register-panel-form">
          <div className="register-form-header">
            <div className="register-form-heading-row">
              <div className="register-form-badge">{meta.badge}</div>
              <Link className="register-home-link" to={meta.loginPath}>
                &larr; Login
              </Link>
            </div>
            <h1 className="register-form-title">{meta.title}</h1>
            <p className="register-form-copy">
              {step === 'email'
                ? meta.copy
                : `Enter the 6-digit code sent to ${email}, then choose a new password.`}
            </p>
          </div>

          <div className="register-role-grid" style={{ marginBottom: '24px' }}>
            {(['customer', 'driver', 'company'] as ResetRole[]).map((role) => {
              const active = role === selectedRole;
              return (
                <button
                  className={`register-role-card${active ? ' register-role-card-active' : ''}`}
                  key={role}
                  onClick={() => switchRole(role)}
                  type="button"
                >
                  <span className="register-role-icon">
                    {role === 'customer' ? 'U' : role === 'driver' ? 'D' : 'C'}
                  </span>
                  <span className="register-role-text">
                    <span className="register-role-title">
                      {role === 'customer' ? 'Customer' : role === 'driver' ? 'Driver' : 'Company Owner'}
                    </span>
                    <span className="register-role-description">
                      {role === 'customer'
                        ? 'Booking account access.'
                        : role === 'driver'
                          ? 'Driver account access.'
                          : 'Company owner access.'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {step === 'email' ? (
            <form
              className="register-form"
              onSubmit={(event) => {
                event.preventDefault();
                void requestResetCode(false);
              }}
            >
              <div className="register-field">
                <label className="register-label" htmlFor="resetEmail">
                  Email Address
                </label>
                <input
                  autoCapitalize="none"
                  className="register-input"
                  id="resetEmail"
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setFieldErrors((current) => ({ ...current, email: '' }));
                  }}
                  placeholder="john@example.com"
                  required
                  type="email"
                  value={email}
                />
                {fieldErrors.email ? <div className="register-field-error">{fieldErrors.email}</div> : null}
              </div>

              {success ? <div className="register-inline-success">{success}</div> : null}
              {error ? <div className="register-inline-error">{error}</div> : null}

              <button className="register-submit" disabled={loading} type="submit">
                {loading ? 'Sending...' : 'Send reset code'}
              </button>
            </form>
          ) : (
            <form className="register-form" onSubmit={submitNewPassword}>
              <div className="register-field-group">
                <div className="register-field">
                  <label className="register-label" htmlFor="resetCode">
                    Reset Code
                  </label>
                  <input
                    className="register-input"
                    id="resetCode"
                    inputMode="numeric"
                    maxLength={6}
                    onChange={(event) => {
                      setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                      setFieldErrors((current) => ({ ...current, code: '' }));
                    }}
                    placeholder="123456"
                    required
                    value={code}
                  />
                  {fieldErrors.code ? <div className="register-field-error">{fieldErrors.code}</div> : null}
                </div>

                <div className="register-field">
                  <label className="register-label" htmlFor="newPassword">
                    New Password
                  </label>
                  <input
                    className="register-input"
                    id="newPassword"
                    onChange={(event) => {
                      setNewPassword(event.target.value);
                      setFieldErrors((current) => ({ ...current, newPassword: '' }));
                    }}
                    placeholder="Enter your new password"
                    required
                    type="password"
                    value={newPassword}
                  />
                  {fieldErrors.newPassword ? <div className="register-field-error">{fieldErrors.newPassword}</div> : null}
                </div>

                <div className="register-field">
                  <label className="register-label" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <input
                    className="register-input"
                    id="confirmPassword"
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                      setFieldErrors((current) => ({ ...current, confirmPassword: '' }));
                    }}
                    placeholder="Confirm your new password"
                    required
                    type="password"
                    value={confirmPassword}
                  />
                  {fieldErrors.confirmPassword ? <div className="register-field-error">{fieldErrors.confirmPassword}</div> : null}
                </div>
              </div>

              {success ? <div className="register-inline-success">{success}</div> : null}
              {error ? <div className="register-inline-error">{error}</div> : null}

              <button className="register-submit" disabled={loading || resending} type="submit">
                {loading ? 'Updating...' : 'Update password'}
              </button>

              <div className="register-secondary-actions">
                <button
                  className="register-text-button"
                  disabled={loading || resending}
                  onClick={() => void requestResetCode(true)}
                  type="button"
                >
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
                <button
                  className="register-text-button"
                  disabled={loading || resending}
                  onClick={() => {
                    setStep('email');
                    setError('');
                    setSuccess('');
                  }}
                  type="button"
                >
                  Change email
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </main>
  );
};

export default ForgotPasswordPage;
